import configLib from '../../../configLib';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { getNewsByAccessId } from '../../store/news';
import { isEmpty } from '../../util/typeGuards';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { GatewayRequestType, GatewayResponseType } from '../web-socket/types';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { DEFAULT_NEWS_PAGE_NUMBER, DEFAULT_NEWS_PAGE_SIZE } from './constants';
import { SymbolNewsSubscriptionRequestType } from './types';

const LOG_PREFIX = '[S-NEWS-SUBS]';
const logger = LoggerService.getLogger();

interface PendingUnsubscription {
  symbol: string;
  timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * Symbol News Subscription Service
 */
export class SymbolNewsSubscriptionService extends BaseSubscriptionService<SymbolNewsSubscriptionRequestType> {
  private static instance: SymbolNewsSubscriptionService | undefined;

  private pendingUnsubscriptions: PendingUnsubscription[] = [];

  // Make the constructor private, to have a single instance
   
  private constructor() {
    super();
  }

  static getInstance(): SymbolNewsSubscriptionService {
    this.instance ??= new SymbolNewsSubscriptionService();
    return this.instance;
  }

  private cancelPendingUnsubscriptions(symbol: string): void {
    this.pendingUnsubscriptions = this.pendingUnsubscriptions.filter(pending => {
      if (pending.symbol === symbol) {
        clearTimeout(pending.timeoutId);
        return false; // Remove from pending list
      }
      return true; // Keep in pending list
    });
  }

  private scheduleDelayedUnsubscription(symbol: string): void {
    const timeoutId = setTimeout(() => {
      this.executeUnsubscription(symbol);
      // Remove from pending list
      this.pendingUnsubscriptions = this.pendingUnsubscriptions.filter(p => p.timeoutId !== timeoutId);
    }, PredefinedMilisecondsTypeEnum.fiveSeconds);

    this.pendingUnsubscriptions.push({ symbol, timeoutId });
  }

  private executeUnsubscription(symbol: string): void {
    this.subscripitonRequests = this.subscripitonRequests.filter(item => !item.symbols.includes(symbol));
  }

  public subscribe(symbol: string, size?: number, page?: number): void {
    this.cancelPendingUnsubscriptions(symbol);

    const pageSize = size ?? DEFAULT_NEWS_PAGE_SIZE;
    const pageNumber = page ?? DEFAULT_NEWS_PAGE_NUMBER;

    if (this.getRequestBySymbol(symbol, pageSize, pageNumber)) { // Already subscribed for this symbol/page!
      return;
    }

    this.sendRequestToGw({
      pageSize,
      pageNumber,
      symbols: [ symbol ],
      ...this.getDefaultRequestParams(),
    });
  }

  public subscribeList = (symbol: string[]): void => {
    symbol.forEach(item => this.subscribe(item));
  };

  public unsubscribe(symbol: string): void {
    const requestBySymbol = this.getRequestBySymbol(symbol);
    if (!requestBySymbol) { // This symbol is not in the list!
      return;
    }

    this.scheduleDelayedUnsubscription(symbol);
  }

  public unsubscribeList = (symbol: string[]): void => {
    symbol.forEach(item => this.unsubscribe(item));
  };

  public handleResponseEvent(event: WebSocketEventData<any>): void {
    const data = event.data ?? {};

    const {
      accessId,
      traceIdentifier,
    } = data || {};

    const traceIdentifierRequest = (this.gatewayService.getRequestedObjectList().find(
      item => item.messageType === this.getSubscriptionRequestType()
        && item.traceIdentifier === traceIdentifier,
    ) ?? {}) as SymbolNewsSubscriptionRequestType;

    const [ symbol ] = traceIdentifierRequest.symbols ?? [];
    if (isEmpty(symbol)) {
      logger.warn(`${LOG_PREFIX} No symbol found in response - this should be handled by NewsSubscriptionService`);
      return;
    }

    configLib.store.dispatch(getNewsByAccessId({
      accessId,
      symbol,
      traceIdentifier,
    }));
  }

  public getSubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.GetLatestNewsArticlesBySymbols;
  }

  public getUnsubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.None;
  }

  protected shouldHandleResponse(event: WebSocketEventData<any>): boolean {
    return event.data?.messageType === this.getResponseType();
  }

  protected getResponseType(): GatewayResponseType {
    return GatewayResponseType.DataQueryResponse;
  }

  private getRequestBySymbol(
    symbol: string,
    pageSize?: number,
    pageNumber?: number,
  ): SymbolNewsSubscriptionRequestType | undefined {
    return this.subscripitonRequests.find(
      item => {
        const symbolMatch = item.symbols.includes(symbol);
        if (pageSize !== undefined && pageNumber !== undefined) {
          return symbolMatch && item.pageSize === pageSize && item.pageNumber === pageNumber;
        }
        return symbolMatch;
      },
    );
  }
}
